import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Attribute } from '@df/catalog/attribute/attribute';
import { IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { ProductConfigurationConfigurable } from '@df/module-product-configuration/model/configurable/product-configuration-configurable';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { optionLabelPipe, OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { PriceComponent } from '@df/ui/price/price.component';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent } from '@icms/core/content';
import each from 'lodash-es/each';
import flatten from 'lodash-es/flatten';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import { Product } from '../../product.decorator';
import { ProductViewCustomOptionsComplexComponent } from '../custom-options/complex/product-view-custom-options-complex.component';
import { NProductViewLayout } from '../layout/product-view-layout.namespace';
import { ProductViewComplexService } from '../product-view-complex.service';
import { ProductViewSwatchComponent } from '../swatch/product-view-swatch.component';
import { ProductViewTooltipComponent } from '../tooltip/product-view-tooltip.component';
import { BoxComponent } from './../../../../common/box/box.component';

enum EComplexStep {
    attributes = 'attributes',
    mounting = 'mounting',
    fabric = 'fabric'
}

interface IComplexStep {
    title: string;
    id: string;
    options: IComplexOptionsMapEntry[];
}

type TProductModel = Product<IProductDataV2> | Product<IProductDataV3>;

interface IConfigurableOptionValue {
    value: number;
    error?: string;
    product?: TProductModel;
}

interface IConfigurableOptionEntry {
    attribute: Attribute;
    values: IConfigurableOptionValue[];
}

type IComplexOptionsMapValue = IConfigurableOptionValue & {
    additionalPrice?: number;
    isCrosssell?: boolean;
};

type IComplexOptionsMapEntry = IConfigurableOptionEntry & {
    values: IComplexOptionsMapValue[];
    bottomText?: string;
    customOptions?: string[];
    displayCustomOptions?: (stepOption: IComplexOptionsMapEntry) => boolean;
};

export const MOUNTING_ATTRIBUTE_ID = 'mounting';
export const Z_CLIP_ORIENTATION_CUSTOM_OPTION_TITLE = 'z-clip orientation';
export const Z_CLIPS_OPTION_LABEL = 'z-clips';

export const SIZE_ATTRIBUTE_ID = 'size';
export const EDGE_TYPE_ATTRIBUTE_ID = 'edge_type';

const COMPLEX_STEPS_CONFIG = [
    {
        title: 'Choose Attributes',
        id: EComplexStep.attributes,
        attributes: [SIZE_ATTRIBUTE_ID, 'thickness']
    },
    {
        title: 'Choose Mounting Options',
        id: EComplexStep.mounting,
        attributes: ['orientation', 'edge_style', EDGE_TYPE_ATTRIBUTE_ID, MOUNTING_ATTRIBUTE_ID]
    },
    {
        title: 'Choose Fabric',
        id: EComplexStep.fabric,
        attributes: ['fabric_style']
    }
];

@Component({
    imports: [
        OptionLabelPipe,
        BoxComponent,
        TranslationModule,
        GridModule,
        CommonModule,
        ProductViewSwatchComponent,
        ToggleClassDirective,
        HideUpComponent,
        PriceComponent,
        ProductViewCustomOptionsComplexComponent,
        HrefComponent,
        DfUiCarouselModule,
        ProductViewTooltipComponent
    ],
    selector: 'product-view-configurable-options',
    templateUrl: './product-view-configurable-options.html',
    styleUrl: './product-view-configurable-options.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewConfigurableOptionsComponent extends UiComponent implements OnInit {
    private initComplex = true;

    @Input()
    @ICmsContent({
        watchDepth: 2
    })
    content?: NProductViewLayout.IBlockDataContent;

    protected readonly configuration = inject(ProductConfiguration) as ProductConfigurationConfigurable;

    protected options?: IConfigurableOptionEntry[];

    protected complexSteps?: IComplexStep[] = [];

    protected toggleService = inject(ToggleService);

    private productViewComplexService = inject(ProductViewComplexService);

    protected toggleId = 'product-view-configurable-options-complex';

    private app = inject(App);

    protected complexStepsIds = EComplexStep;

    private router = inject(Router);

    private preventToggleReset = false;

    protected zClipOrientationCustomOptionTitle = Z_CLIP_ORIENTATION_CUSTOM_OPTION_TITLE;

    override ngOnInit(): void {
        super.ngOnInit();
        const initSimple = this.getInitSimple();
        this.productViewComplexService.product = undefined;
        if (initSimple && this.product && initSimple.id !== this.product.id) {
            const selectionObject = initSimple.createSelectionObject();
            if (selectionObject) {
                each(selectionObject, (value, key) => {
                    if (value) {
                        this.configuration.select(key, value);
                    }
                });
                this.toggleService.open([this.toggleId, 2]);
                this.init();
            } else {
                this.init();
            }
        } else {
            this.init();
        }
    }

    private getInitSimple(): Product<IProductDataV3> | undefined {
        const previousSimple = this.productViewComplexService.product;
        if (previousSimple?.isSimple) {
            const previousSelection = previousSimple.createSelectionObject();
            if (previousSelection) {
                delete previousSelection['fabric_style'];
                const initSimple = this.parent.matchChild(previousSelection, true);
                return initSimple as Product<IProductDataV3> | undefined;
            }
        }
        return undefined;
    }

    private init(): void {
        this.subscriptions.add(this.configuration.options$.subscribe(() => this.onOptionsChange()));
        this.subscriptions.add(this.configuration.selectionChange$.subscribe(() => this.onSelectionChange()));
        this.subscriptions.add(
            this.configuration.product$.subscribe(() => {
                this.updateComplexStepsDebounced();
                if (!this.toggleService.isGroupOpen(this.toggleId)) {
                    this.toggleService.open([this.toggleId, 0]);
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (!this.preventToggleReset) {
            this.toggleService.closeGroup(this.toggleId);
        }
    }

    private onOptionsChange() {
        this.options = this.configuration.options$.value as unknown as IConfigurableOptionEntry[];
        if (this.initComplex) {
            this.initComplex = false;
            this.updateComplexSteps();
        } else {
            this.updateComplexStepsDebounced();
        }
        this.detectChanges();
    }

    private onSelectionChange() {
        this.detectChanges();
    }

    private updateComplexSteps(): void {
        this.complexSteps = this.isComplex && this.options ? this.getComplexSteps(this.options) : undefined;
        this.detectChanges();
    }

    private updateComplexStepsDebounced = this.timeoutService.debounce(() => this.updateComplexSteps(), 100);

    private getComplexSteps(options: IConfigurableOptionEntry[]): IComplexStep[] {
        const steps: IComplexStep[] = COMPLEX_STEPS_CONFIG.map(i => {
            const stepOptions = i.attributes
                .map(attribute => {
                    const option = options.find(i => i.attribute.id === attribute);
                    if (option) {
                        return {
                            attribute: option.attribute,
                            values: [...option.values]
                        };
                    }
                    return undefined;
                })
                .filter(i => !!i) as IComplexOptionsMapEntry[];

            if (i.id === EComplexStep.mounting) {
                stepOptions.forEach(stepOption => {
                    const simpleProduct = this.product.isSimple ? this.product : this.parent?.children[0];
                    if (simpleProduct) {
                        const values = stepOption.values.map(i => i.value);
                        const attrCode = stepOption.attribute.id;
                        const selectionObject = { ...simpleProduct.createSelectionObject() };
                        if (selectionObject) {
                            delete selectionObject[attrCode];

                            const optionProducts =
                                this.parent.matchChildren(selectionObject, true)?.filter(i => values.indexOf(i.data?.[attrCode]) > -1) ||
                                [];
                            const cheapest = sortBy(optionProducts, product => product.price)[0];
                            const cheapestPrice = cheapest?.price;
                            stepOption.values.forEach(stepOtionValue => {
                                let additionalPrice = 0;
                                if (cheapestPrice) {
                                    const valueSelectionObject = { ...selectionObject };
                                    valueSelectionObject[attrCode] = stepOtionValue.value;
                                    const simpleProduct = this.parent.matchChild(valueSelectionObject);
                                    if (simpleProduct) {
                                        const price = simpleProduct.price;
                                        if (price) {
                                            const diff = (price * 100 - cheapestPrice * 100) / 100;
                                            if (diff > 0) {
                                                additionalPrice = diff;
                                            }
                                        }
                                    }
                                }
                                stepOtionValue['additionalPrice'] = additionalPrice;
                            });
                        }
                    }
                });
            }
            stepOptions.forEach(i => {
                if (i.attribute.id === 'edge_style') {
                    const miterValue = i.values.find(j => {
                        const label = i.attribute.getOptionLabel(j.value);
                        if (typeof label === 'string') {
                            return label.toLowerCase().indexOf('miter') > -1;
                        }
                    });
                    if (miterValue) {
                        i.bottomText = '*Mitered edges require edge hardening.';
                    }
                }
            });
            const step: IComplexStep = {
                title: i.title,
                id: i.id,
                options: stepOptions
            };

            return step;
        });

        const usedAttributes = flatten(steps.map(i => i.options.map(j => j.attribute.id)));
        options.forEach(option => {
            if (usedAttributes.indexOf(option.attribute.id) === -1) {
                steps[0].options.push(option);
            }
        });

        const fabricStep = steps.find(i => i.id === EComplexStep.fabric);
        if (fabricStep) {
            const crossselValues = this.getFabricStyleCrosssellValues();
            if (crossselValues?.length) {
                const fabricStyleOption = fabricStep.options.find(i => i.attribute.id === 'fabric_style');
                if (fabricStyleOption) {
                    if (fabricStyleOption.values.length === 1) {
                        fabricStyleOption.values = uniqBy([...fabricStyleOption.values, ...crossselValues], value => value.value);
                    }
                } else {
                    const fabricStyleAttribute = this.app.resources.attribute.getExisting('fabric_style');
                    if (fabricStyleAttribute) {
                        fabricStep.options.push({ attribute: fabricStyleAttribute, values: crossselValues });
                    }
                }
            }
            const fabricStyleOption = fabricStep.options.find(i => i.attribute.id === 'fabric_style');
            if (fabricStyleOption?.values?.length) {
                fabricStyleOption.values = sortBy(fabricStyleOption.values, i => {
                    return optionLabelPipe(i.value, 'fabric_style', true);
                });
            }

            steps.forEach(step => {
                const mountingOption = step.options.find(i => i.attribute.id === MOUNTING_ATTRIBUTE_ID);
                if (mountingOption) {
                    mountingOption.customOptions = [Z_CLIP_ORIENTATION_CUSTOM_OPTION_TITLE];
                    mountingOption.displayCustomOptions = (stepOption: IComplexOptionsMapEntry) => {
                        return this.displayCustomOptions(stepOption);
                    };
                }
            });
        }
        return steps;
    }

    private displayCustomOptions(stepOption: IComplexOptionsMapEntry): boolean {
        if (stepOption.customOptions?.length) {
            const attribute = stepOption.attribute;
            return !!stepOption?.customOptions?.find(i => {
                if (i === Z_CLIP_ORIENTATION_CUSTOM_OPTION_TITLE) {
                    const zClipsOption = attribute.data?.options?.find(j => j.label?.toString()?.toLowerCase() === 'z-clips');
                    if (zClipsOption) {
                        return this.configuration.isSelected(attribute.id, zClipsOption.value);
                    }
                }
                return false;
            });
        }
        return false;
    }

    private getFabricStyleCrosssellValues(): IComplexOptionsMapValue[] {
        const crosssells = this.parent?.data?.link?.crossSell;
        if (crosssells?.length) {
            const crosssellProductsWithFabricStyle = (
                this.app.resources.product.getAllExisting(crosssells) as Product<IProductDataV2>[]
            )?.filter(i => i.data?.fabric_style);
            if (crosssellProductsWithFabricStyle?.length) {
                return crosssellProductsWithFabricStyle
                    .map(i => {
                        const fabricStyle = i.data?.fabric_style;
                        if (fabricStyle) {
                            const value = Array.isArray(fabricStyle) ? fabricStyle[0] : fabricStyle;
                            if (value) {
                                return {
                                    value,
                                    product: i,
                                    isCrosssell: true
                                };
                            }
                        }
                        return undefined;
                    })
                    .filter(i => !!i);
            }
        }
        return [];
    }

    protected get isComplex(): boolean {
        return this.product.isComplexPanel;
    }

    private get product(): Product<IProductDataV3> {
        return this.configuration.product$.value as Product<IProductDataV3>;
    }

    private get parent(): Product<IProductDataV3> {
        return this.product.parent as Product<IProductDataV3>;
    }

    protected selectCrosssell(product: Product<IProductDataV2>): void {
        if (product?.data?.url) {
            if (this.product?.isSimple) {
                this.productViewComplexService.product = this.product;
                this.productViewComplexService.customOptions = this.configuration.customOptions$.value;
            }
            this.preventToggleReset = true;
            this.router.navigateByUrl(product.data.url);
        }
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    get pcSlides(): number {
        return 3.15;
    }

    get pcSlidesCeil(): number {
        return Math.ceil(this.pcSlides);
    }

    protected useGrid(attributeCode: string): boolean {
        return ['thickness', 'size'].indexOf(attributeCode) > -1;
    }

    protected get additionalLeadTime(): string | undefined {
        return this.content?.additionalLeadTimeText;
    }

    protected get fabricStyle(): number | undefined {
        const fabricStyle = this.product.getAttr('fabric_style') as number | number[];
        return typeof fabricStyle === 'number' ? fabricStyle : undefined;
    }

    protected isCrosssellSelected(option: IComplexOptionsMapEntry, value: IComplexOptionsMapValue): boolean {
        const id = option.attribute.id;
        const productAttrValue = this.product.getAttr(id) as number | number[];
        const productValue = Array.isArray(productAttrValue) ? productAttrValue[0] : productAttrValue;
        return productValue === value.value;
    }
}
